import { useParams, useNavigate } from 'react-router-dom';
import { useEmployeeInvoiceDetail, useUpdateInvoiceState } from '../hooks/useEmployeeInvoices';
import { StatusBadge } from '../components/workflow/StatusBadge';
import { Spinner } from '../components/common/Spinner';
import { useAuditTrail } from '../hooks/useAuditTrail';
import { StateHistory } from '../components/workflow/StateHistory';
import { DEDUCTED_FIELD_LABELS } from '../services/api/employeeSalary';
import { formatMonthYear } from '../utils/dateFormat';
import { formatDisplayDate } from '../utils/format';
import { LEGACY_CZ_NUMBER_LOCALE, formatNumber } from '../utils/localeFormat';

function formatAmount(value: number): string {
  return formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE);
}

export default function EmployeeInvoiceDetailPage() {
  const { year: yearParam, month: monthParam } = useParams<{ year: string; month: string }>();
  const navigate = useNavigate();
  const year = yearParam ? parseInt(yearParam, 10) : NaN;
  const month = monthParam ? parseInt(monthParam, 10) : NaN;
  const validParams = !isNaN(year) && !isNaN(month) && year > 0 && month >= 1 && month <= 12;

  const { data: invoice, isLoading, error } = useEmployeeInvoiceDetail(
    validParams ? year : 0,
    validParams ? month : 0,
  );
  const updateStateMutation = useUpdateInvoiceState();
  const { data: auditTrail } = useAuditTrail('EmployeeInvoice', invoice?.id ?? 0);

  if (!validParams) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Invalid URL: year and month parameters are required (e.g. /invoices/2025/3).</p>
        <button onClick={() => navigate(-1)} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
          &larr; Back to Dashboard
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error ? (error as Error).message : 'No invoice data for this period'}</p>
        <button onClick={() => navigate(-1)} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
          &larr; Back to Dashboard
        </button>
      </div>
    );
  }

  // MKS-351 — "October 2026" from the shared util.
  const monthName = formatMonthYear(new Date(invoice.year, invoice.month - 1));

  // MKS-125 — advisory when this open invoice inherits deductions from an
  // earlier month that is still Open (deductions re-apply until it's Sent).
  const formatPeriod = (y: number, m: number) => formatMonthYear(new Date(y, m - 1));
  const earlierOpenMonths = invoice.warning?.type === 'earlier-months-open'
    ? invoice.warning.earlierOpenMonths
    : [];
  const single = earlierOpenMonths.length === 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
            &larr; Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Invoice: {monthName}</h1>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge state={invoice.state} size="lg" />
          {invoice.state === 'open' ? (
            <button
              onClick={() => updateStateMutation.mutate({ year, month, state: 'sent' })}
              disabled={updateStateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Mark as Sent
            </button>
          ) : (
            <button
              onClick={() => updateStateMutation.mutate({ year, month, state: 'open' })}
              disabled={updateStateMutation.isPending}
              className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              Revert to Open
            </button>
          )}
        </div>
      </div>

      {/* MKS-125 — earlier-month-still-open advisory */}
      {earlierOpenMonths.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-300 p-4">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Heads up:</span>{' '}
            {single ? 'An earlier month' : 'Earlier months'} (
            {earlierOpenMonths.map((m) => formatPeriod(m.year, m.month)).join(', ')})
            {single ? ' is' : ' are'} still Open, so {single ? 'its' : 'their'} deductions
            re-apply on this invoice. Mark {single ? 'it' : 'them'} as Sent (oldest first)
            so each deduction is only applied once.
          </p>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Total Hours</div>
          <div className="text-xl font-semibold text-gray-900">{invoice.totalHours.toFixed(1)}h</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Billing</div>
          <div className="text-xl font-semibold text-gray-900">{formatNumber(invoice.totalBilling, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Expenses</div>
          <div className="text-xl font-semibold text-gray-900">{formatNumber(invoice.totalExpenses, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</div>
        </div>
        {invoice.totalExtraCosts > 0 && (
          <div className="bg-white rounded-lg border border-red-200 p-4">
            <div className="text-sm text-gray-500">Extra Costs</div>
            <div className="text-xl font-semibold text-red-600">-{formatNumber(invoice.totalExtraCosts, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</div>
          </div>
        )}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Net Amount</div>
          <div className="text-xl font-semibold text-gray-900">{formatNumber(invoice.netAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</div>
        </div>
      </div>

      {/* By Project */}
      {invoice.byProject.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">By Project</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hours × Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.byProject.map((project, idx) => (
                  <tr key={`${project.projectId}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.projectName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{project.hours.toFixed(1)}h × {formatNumber(project.rate, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)} Kč</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(project.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{invoice.totalHours.toFixed(1)}h</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(invoice.totalBilling, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expenses */}
      {invoice.expenses.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Expenses</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.expenses.map((expense) => (
                  <tr key={expense.expenseSheetId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{expense.projectName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{expense.description || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(expense.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900" colSpan={2}>Total</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatNumber(invoice.totalExpenses, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Extra Costs (Deductions) */}
      {invoice.extraCosts && invoice.extraCosts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Extra Costs (Deductions)</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Deducted</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.extraCosts.map((ec) => (
                  <tr key={ec.extraCostId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{ec.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDisplayDate(ec.costDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right font-medium">
                      -{formatNumber(ec.appliedAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900" colSpan={2}>Total Deducted</td>
                  <td className="px-4 py-3 text-sm text-red-600 text-right">
                    -{formatNumber(invoice.totalExtraCosts, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {invoice.carryOver > 0 && (
            <div className="mt-3 rounded-lg bg-yellow-50 border border-yellow-200 p-3">
              <p className="text-sm text-yellow-800">
                {formatNumber(invoice.carryOver, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)} CZK could not be deducted this month and will carry over to the next invoice.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Grand Total — Phase 84: when a salary is deducted, show the whole
          derivation. Someone in concurrent employment needs to see WHY the
          figure to invoice is lower than what they billed; a bare number that
          silently disagrees with the Billing card above reads as a bug. */}
      <div className="bg-white rounded-lg border-2 border-gray-900 p-4 space-y-2">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Billing</span>
          <span>{formatAmount(invoice.totalBilling)} CZK</span>
        </div>
        {invoice.totalExtraCosts > 0 && (
          <div className="flex justify-between items-center text-sm text-red-600">
            <span>Extra costs</span>
            <span>-{formatAmount(invoice.totalExtraCosts)} CZK</span>
          </div>
        )}
        {invoice.salaryAmount != null && invoice.salaryAmount > 0 && (
          <div className="flex justify-between items-center text-sm text-red-600">
            <span>
              {invoice.deductedField
                ? `${DEDUCTED_FIELD_LABELS[invoice.deductedField]} deduction`
                : 'Salary deduction'}
            </span>
            <span>-{formatAmount(invoice.salaryAmount)} CZK</span>
          </div>
        )}
        {/* Shown even at 0 so the chain always reads end-to-end — a line that
            appears only sometimes makes the arithmetic look like it skipped a
            step. Expenses are reimbursed on top of the deducted earnings, so
            they are never eaten by a salary. */}
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Expenses</span>
          <span>+{formatAmount(invoice.totalExpenses)} CZK</span>
        </div>
        <div className="flex justify-between items-center border-t border-gray-200 pt-2">
          <span className="text-lg font-semibold text-gray-900">To be invoiced</span>
          <span className="text-lg font-semibold text-gray-900">
            {formatAmount(invoice.invoice ?? invoice.netAmount)} CZK without VAT
          </span>
        </div>
      </div>

      {/* History */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
        <StateHistory history={auditTrail?.data ?? []} />
      </div>
    </div>
  );
}
